'use client';

import { useEffect, useId, useRef, type ReactElement } from 'react';
import FocusLock from 'react-focus-lock';

import { useTranslations } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';

import Button from '../button';
import { IconCloseAlt } from '../icon';

import FindAReservationForm from './find-a-reservation-form';
import type {
	FindAReservationAnalyticsEvent,
	FindAReservationBackdropScope,
	FindAReservationSubmitData,
	FoundReservationData,
	ResortOptionData,
} from './types';

const COPY = {
	heading: 'Find a Reservation',
	subHeading: 'Please enter your reservation details.',
	close: 'Close',
} as const;

export type Props = {
	isOpen: boolean;
	onDismiss: () => void;
	onSubmit: (values: FindAReservationSubmitData) => Promise<FoundReservationData | void>;
	onSuccess: (reservation?: FoundReservationData) => void;
	onAnalyticsEvent?: (event: FindAReservationAnalyticsEvent) => void;
	singleProperty?: { label: string; value: string };
	properties?: ResortOptionData[];
	locale?: string;
	backdropScope?: FindAReservationBackdropScope;
	disableSubmitUntilComplete?: boolean;
	supportPhoneNumber?: string;
	supportPhoneHref?: string;
	tollFreeNumbersHref?: string;
	className?: string;
	panelClassName?: string;
};

export default function FindAReservationModal({
	isOpen,
	onDismiss,
	onSubmit,
	onSuccess,
	onAnalyticsEvent,
	singleProperty,
	properties,
	backdropScope = 'page',
	disableSubmitUntilComplete = true,
	supportPhoneNumber,
	supportPhoneHref,
	tollFreeNumbersHref,
	className,
	panelClassName,
}: Props): ReactElement | null {
	const t = useTranslations();
	const panelRef = useRef<HTMLDivElement>(null);
	const analyticsRef = useRef(onAnalyticsEvent);
	const uid = `far-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
	const headingId = `${uid}-heading`;
	const descriptionId = `${uid}-description`;

	useEffect(() => {
		analyticsRef.current = onAnalyticsEvent;
	});

	useEffect(() => {
		if (!isOpen) return;
		analyticsRef.current?.('form-shown');
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onDismiss();
		};
		const handlePointerDown = (event: MouseEvent) => {
			const target = event.target as Node | null;
			if (!target || !document.contains(target)) return;
			if (panelRef.current?.contains(target)) return;
			onDismiss();
		};

		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('mousedown', handlePointerDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('mousedown', handlePointerDown);
		};
	}, [isOpen, onDismiss]);

	useEffect(() => {
		if (!isOpen) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div
			data-testid="find-a-reservation-overlay"
			className={cn(
				'inset-0 z-[1000] flex items-center justify-center',
				backdropScope === 'page' ? 'fixed' : 'absolute max-[767px]:fixed',
				className
			)}
		>
			<div
				data-testid="find-a-reservation-backdrop"
				aria-hidden="true"
				className={cn(
					'bg-backdrop',
					backdropScope === 'page' ? 'fixed inset-0' : 'absolute inset-0'
				)}
			/>

			<FocusLock
				returnFocus
				className={cn(
					'relative flex max-h-full w-full max-w-[507px] justify-center',
					'max-[767px]:h-full max-[767px]:max-w-none'
				)}
			>
				<div
					ref={panelRef}
					role="dialog"
					aria-modal="true"
					aria-labelledby={headingId}
					aria-describedby={descriptionId}
					className={cn(
						'bg-surface-default relative flex w-full flex-col overflow-y-auto rounded-[20px] px-300 pt-500 pb-400 shadow-[0px_5px_15px_0px_rgba(3,3,3,0.12)]',
						'max-[767px]:h-full max-[767px]:rounded-none max-[767px]:shadow-none',
						panelClassName
					)}
				>
					<Button
						intent="unstyled"
						onClick={onDismiss}
						data-autofocus
						aria-label={t(COPY.close)}
						data-testid="find-a-reservation-close"
						className="absolute top-[8px] right-[8px] flex h-[44px] w-[44px] items-center justify-center cursor-pointer"
					>
						<IconCloseAlt width={24} height={24} />
					</Button>

					<h2 id={headingId} className="text-center text-subtitle1">
						{t(COPY.heading)}
					</h2>
					<p id={descriptionId} className="mt-100 mb-300 text-center text-body3">
						{t(COPY.subHeading)}
					</p>

					<FindAReservationForm
						idPrefix={uid}
						singleProperty={singleProperty}
						properties={properties}
						disableSubmitUntilComplete={disableSubmitUntilComplete}
						supportPhoneNumber={supportPhoneNumber}
						supportPhoneHref={supportPhoneHref}
						tollFreeNumbersHref={tollFreeNumbersHref}
						onSubmit={onSubmit}
						onSuccess={onSuccess}
						onAnalyticsEvent={onAnalyticsEvent}
					/>
				</div>
			</FocusLock>
		</div>
	);
}
